// SmartAI-Panel — schwebender Einstiegsbutton der KI-Rezeption (Public Site).
//
// Sichtbarkeit wird zur LAUFZEIT über GET /api/public/smartai/config entschieden
// (Vendor-Feature-Flag + visibility-Setting), nicht zur Build-Zeit:
//   - Feature aus oder Config nicht erreichbar => kein Button (fail-closed).
//   - visibility 'preview' => Button nur mit ?smartai=preview (sessionStorage-
//     persistent), für Tests auf der Live-Seite unsichtbar für Besucher.
// Der Panel-Inhalt ist code-gesplittet und wird erst beim ersten Öffnen
// geladen — der Prerender-Snapshot enthält damit höchstens den Button.

import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, PhoneIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { PHONE_HREF } from '../../config/contact';
import { SMARTAI_OPEN_EVENT, SmartAIOpenDetail } from './openSmartAI';
import type { HomeAction } from './v2/SmartAIHomeV2';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSmartAIConfig, SmartAIConfig } from '../../services/smartaiPublicApi';
import { formatLongDate } from '../../utils/formatLongDate';

const SmartAIPanelBody = lazy(() => import('./SmartAIPanelBody'));
const SmartAIPanelBodyV2 = lazy(() => import('./v2/SmartAIPanelBodyV2'));

const PREVIEW_KEY = 'smartai_preview';

const isPreviewSession = (search: string): boolean => {
  try {
    if (new URLSearchParams(search).get('smartai') === 'preview') {
      sessionStorage.setItem(PREVIEW_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(PREVIEW_KEY) === '1';
  } catch {
    return false;
  }
};

const SmartAIPanel: React.FC = () => {
  const { translations, language } = useLanguage();
  const location = useLocation();
  const [config, setConfig] = useState<SmartAIConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false); // einmal geöffnet => Body bleibt gemountet
  // Von außen gewünschte Startansicht (z.B. Rückruf aus dem Hero). Mit Zähler,
  // damit derselbe Wunsch zweimal hintereinander auch zweimal greift.
  const [requested, setRequested] = useState<{ action: HomeAction; nonce: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSmartAIConfig().then((c) => { if (!cancelled) setConfig(c); });
    return () => { cancelled = true; };
  }, []);

  const visible = Boolean(config && config.enabled
    && (config.visibility !== 'preview' || isPreviewSession(location.search)));
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Öffnen auf Zuruf (siehe openSmartAI.ts). preventDefault = „übernommen".
  useEffect(() => {
    const onOpen = (e: Event) => {
      if (!visibleRef.current) return;
      e.preventDefault();
      const action = (e as CustomEvent<SmartAIOpenDetail>).detail?.action;
      if (action) setRequested((prev) => ({ action, nonce: (prev?.nonce ?? 0) + 1 }));
      setOpen(true);
      setOpened(true);
    };
    window.addEventListener(SMARTAI_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SMARTAI_OPEN_EVENT, onOpen);
  }, []);

  // Solange der Einstieg unten sichtbar ist, trägt er mobil den Notruf als
  // Segment mit — der separate Notruf-Knopf (EmergencyButton) blendet sich
  // dann dort aus (index.css, html[data-smartai-dock]). Ein Dock statt zwei
  // konkurrierender Blasen.
  useEffect(() => {
    if (!visible) return;
    document.documentElement.setAttribute('data-smartai-dock', '1');
    return () => document.documentElement.removeAttribute('data-smartai-dock');
  }, [visible]);

  if (!config || !visible) return null;

  const t = translations.smartai || {};

  const openPanel = () => { setOpen(true); setOpened(true); };
  const phoneLabel = translations.emergency?.common?.callNow
    || translations.emergency?.call
    || 'Notfall: 603 31 96 48';

  return (
    <>
      {/* Einstieg unten. Mobil ein einziges Dock über die volle Breite: links
          das Anliegen, rechts der Notruf als Segment. Ab Tablet steht nur der
          Einstieg unten links, der Notruf bleibt dort ein eigener Knopf
          unten rechts (EmergencyButton). */}
      {!open && (
        <div
          className="fixed bottom-4 left-4 right-4 md:right-auto md:w-fit z-50 flex items-stretch rounded-full shadow-lg border-2 border-white overflow-hidden"
          data-testid="smartai-dock"
        >
          <button
            type="button"
            onClick={openPanel}
            className="flex-1 md:flex-none flex items-center justify-center bg-ds-primary text-white hover:bg-ds-primary-container py-2.5 px-4 transition-colors"
            aria-label={t.buttonLabel || 'Termin, Rezept oder Frage?'}
          >
            <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2 shrink-0" aria-hidden="true" />
            <span className="font-bold text-sm text-left leading-snug">{t.buttonLabel || 'Termin, Rezept oder Frage?'}</span>
          </button>
          <a
            href={PHONE_HREF}
            aria-label={phoneLabel}
            title={phoneLabel}
            className="md:hidden w-14 shrink-0 flex items-center justify-center bg-coral text-white border-l-2 border-white"
          >
            <PhoneIcon className="w-6 h-6" aria-hidden="true" />
          </a>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] md:inset-auto md:bottom-4 md:left-4 md:w-[380px] md:max-h-[80vh] flex flex-col bg-white md:rounded-2xl shadow-2xl md:border md:border-ds-outline-variant overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label={t.title || 'Digitale Rezeption'}
        >
          <div className="flex items-start justify-between bg-ds-primary text-white px-4 py-3">
            <div>
              <div className="font-bold">{t.title || 'Digitale Rezeption'}</div>
              {/* KI-Transparenzhinweis (EU AI Act) — immer sichtbar im Kopf */}
              <div className="text-xs text-white/70">{t.aiTransparency || 'Digitaler Assistent — kein Mensch.'}</div>
              {config.layout === 'v2' && config.hours.openNow !== null && (
                <div className="flex items-center gap-2 mt-1.5 text-xs text-white/70">
                  <span className={`w-[7px] h-[7px] rounded-full shrink-0 ${config.hours.openNow ? 'bg-ds-success-green' : 'bg-white/40'}`} />
                  <span>
                    {config.hours.closedUntil
                      ? `${translations.smartai?.v2?.closedHoliday || 'Betriebsferien bis einschließlich'} ${formatLongDate(config.hours.closedUntil, language)}`
                      : config.hours.openNow
                        ? `${translations.smartai?.v2?.openUntil || 'Geöffnet bis'} ${config.hours.closesAt}`
                        : `${translations.smartai?.v2?.closedNow || 'Geschlossen · wieder ab'} ${config.hours.opensAt}`}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-full hover:bg-white/10"
              aria-label={t.close || 'Schließen'}
            >
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <Suspense fallback={<div className="p-6 text-sm text-ds-on-surface-variant">…</div>}>
              {opened && (config.layout === 'v2'
                ? <SmartAIPanelBodyV2 config={config} onClose={() => setOpen(false)} requested={requested} />
                : <SmartAIPanelBody config={config} onClose={() => setOpen(false)} />)}
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

export default SmartAIPanel;
